import React, { useState, useEffect } from 'react';
import { calculateBusinessTax, formatCurrency } from '../../utils/taxCalculations';

const BusinessInvestmentForm = ({ data, updateData, businessDetails, businessExpenses, nextStep, prevStep, isLastStep, isProcessing }) => {
    const [formData, setFormData] = useState({
        // B. Investment & Growth
        newMachineryEquipment: data?.newMachineryEquipment || '',
        vehiclePurchase: data?.vehiclePurchase || '',
        officeExpansion: data?.officeExpansion || '',
        technologyUpgradation: data?.technologyUpgradation || '',

        marketResearch: data?.marketResearch || '',
        productDevelopment: data?.productDevelopment || '',
        trainingDevelopment: data?.trainingDevelopment || '',

        // Other Income
        savingInterest: data?.savingInterest || '',
        fdInterest: data?.fdInterest || '',
        rdInterest: data?.rdInterest || '',
        licMaturity: data?.licMaturity || '',
        agriIncome: data?.agriIncome || '',
    });

    const [expandedSections, setExpandedSections] = useState({
        capital: false,
        development: false,
        otherIncome: false,
    });

    const [taxCalculation, setTaxCalculation] = useState(null);

    // Calculate tax whenever form data changes
    useEffect(() => {
        const salesTurnover = parseFloat(businessDetails?.grossTurnover || businessDetails?.partnershipTurnover || 0);

        // Calculate total expenses
        const totalExpenses = Object.keys({ ...businessDetails, ...businessExpenses }).reduce((sum, key) => {
            if (key !== 'businessType' && key !== 'grossTurnover' && key !== 'totalProfit' &&
                key !== 'partnershipTurnover' && key !== 'taxableProfit') {
                return sum + (parseFloat(businessDetails[key] || businessExpenses[key] || 0));
            }
            return sum;
        }, 0);

        // Calculate other income
        const otherIncome = (parseFloat(formData?.savingInterest || 0)) +
            (parseFloat(formData?.fdInterest || 0)) +
            (parseFloat(formData?.rdInterest || 0)) +
            (parseFloat(formData?.licMaturity || 0)) +
            (parseFloat(formData?.agriIncome || 0));

        const calculation = calculateBusinessTax({
            salesTurnover,
            otherIncome,
            totalExpenses
        });

        setTaxCalculation(calculation);
    }, [formData, businessDetails, businessExpenses]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        updateData(formData);
        nextStep(formData);
    };

    return (
        <div className="form-step">
            <h2 className="step-title">Business Investment & Other Income</h2>
            <p className="step-description">Final step: Investment plans and other income sources</p>

            <form onSubmit={handleSubmit} className="diagnosis-form">

                {/* B. INVESTMENT & GROWTH */}
                <h3 className="section-heading">  Business Investment & Growth</h3>

                {/* Capital Expenditure */}
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('capital')}>
                        1. Capital Expenditure
                        <span className="toggle-icon">{expandedSections.capital ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.capital && (
                        <div className="grid-2-col">
                            <InputGroup label="New Machinery/Equipment" name="newMachineryEquipment" value={formData.newMachineryEquipment} onChange={handleChange} />
                            <InputGroup label="Vehicle Purchase" name="vehiclePurchase" value={formData.vehiclePurchase} onChange={handleChange} />
                            <InputGroup label="Office Expansion" name="officeExpansion" value={formData.officeExpansion} onChange={handleChange} />
                            <InputGroup label="Technology Upgradation" name="technologyUpgradation" value={formData.technologyUpgradation} onChange={handleChange} />
                        </div>
                    )}
                </div>

                {/* Business Development */}
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('development')}>
                        2. Business Development
                        <span className="toggle-icon">{expandedSections.development ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.development && (
                        <>
                            <div className="grid-2-col">
                                <InputGroup label="Market Research" name="marketResearch" value={formData.marketResearch} onChange={handleChange} />
                                <InputGroup label="Product Development" name="productDevelopment" value={formData.productDevelopment} onChange={handleChange} />
                            </div>
                            <InputGroup label="Training & Development" name="trainingDevelopment" value={formData.trainingDevelopment} onChange={handleChange} />
                        </>
                    )}
                </div>

                {/* Other Income Sources */}
                <h3 className="section-heading" style={{ marginTop: '30px' }}> Other Income Sources</h3>
                <div className="expense-section">
                    <h4 className="subsection-title collapsible" onClick={() => toggleSection('otherIncome')}>
                        Other Income Details
                        <span className="toggle-icon">{expandedSections.otherIncome ? '▼' : '▶'}</span>
                    </h4>
                    {expandedSections.otherIncome && (
                        <>
                            <div className="grid-2-col">
                                <InputGroup label="Saving Bank Interest" name="savingInterest" value={formData.savingInterest} onChange={handleChange} />
                                <InputGroup label="FD Interest" name="fdInterest" value={formData.fdInterest} onChange={handleChange} />
                                <InputGroup label="RD Interest" name="rdInterest" value={formData.rdInterest} onChange={handleChange} />
                                <InputGroup label="LIC Maturity" name="licMaturity" value={formData.licMaturity} onChange={handleChange} />
                            </div>
                            <InputGroup label="Agriculture Income" name="agriIncome" value={formData.agriIncome} onChange={handleChange} />
                        </>
                    )}
                </div>





                {/* Form Actions */}
                <div className="form-actions">
                    <button type="button" onClick={() => { updateData(formData); prevStep(); }} className="btn-secondary">← Back</button>
                    <button type="submit" className="btn-primary" disabled={isProcessing}>
                        {isProcessing ? 'Processing...' : (isLastStep ? 'Generate Report →' : 'Next Step →')}
                    </button>
                </div>
            </form>

            <style jsx>{`
                .section-heading {
                    color: #1f2937;
                    margin-bottom: 15px;
                    margin-top: 20px;
                    border-bottom: 2px solid #f3f4f6;
                    padding-bottom: 8px;
                    font-size: 1.1rem;
                }
                
                .expense-section {
                    background: #f9fafb;
                    padding: 15px;
                    border-radius: 8px;
                    margin-bottom: 15px;
                }
                
                .subsection-title {
                    color: #4b5563;
                    margin-bottom: 12px;
                    font-size: 0.95rem;
                    font-weight: 600;
                }
                
                .subsection-title.collapsible {
                    cursor: pointer;
                    user-select: none;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 10px;
                    background: #fff;
                    border-radius: 6px;
                    transition: background-color 0.2s;
                }
                
                .subsection-title.collapsible:hover {
                    background: #f3f4f6;
                }
                
                .toggle-icon {
                    font-size: 0.8rem;
                    color: #6b7280;
                }
                
                .grid-2-col {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 15px;
                }
                
                @media (max-width: 768px) {
                    .grid-2-col {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

const InputGroup = ({ label, name, value, onChange }) => {
    const inputValue = value === null || value === undefined ? '' : String(value);

    return (
        <div className="form-group" style={{ marginBottom: '12px' }}>
            <label style={{ fontSize: '0.9rem', marginBottom: '4px', display: 'block', color: '#374151' }}>
                {label}
            </label>
            <div className="input-with-icon" style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }}>₹</span>
                <input
                    type="number"
                    name={name}
                    value={inputValue}
                    onChange={onChange}
                    placeholder="0"
                    style={{
                        paddingLeft: '25px',
                        width: '100%',
                        padding: '8px 10px 8px 25px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db'
                    }}
                    onWheel={(e) => {
                        e.target.blur();
                        e.stopPropagation();
                    }}
                    onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
                            e.preventDefault();
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default BusinessInvestmentForm;
